<template>
	<div
		v-if="isLoading"
		class="h-full pt-4 md:pt-8 lg:pt-16 px-2 md:px-8 lg:px-16"
	>
		<div
			class="px-8 py-4 border border-accent-green flex items-center justify-center text-center rounded-lg"
		>
			<div class="text-vc-small">Cargando producto...</div>
		</div>
	</div>
	<div
		v-else-if="product"
		class="h-full"
	>
		<div
			class="h-full pt-4 md:pt-8 lg:pt-16 px-2 md:px-8 lg:px-16 block lg:flex flex-row space-x-0 lg:space-x-8"
		>
			<div class="w-full lg:w-1/2">
				<ProductPhotoComponent
					class="aspect-square flex-none overflow-hidden rounded-lg"
					:photo="product.image"
				/>
			</div>

			<div
				class="w-full lg:w-1/2 pt-4 lg:pt-0 px-2 lg:px-0 lg:h-[calc(100vh-8rem)] lg:overflow-y-auto"
			>
				<div class="text-vc-title-sm font-medium leading-tight">
					{{ product.title.split(' - ')[0] }}
				</div>
				<div class="text-vc-header-sm leading-tight">
					{{ product.title.split(' - ')[1] }}
				</div>

				<div class="flex items-center justify-between mt-4 select-none">
					<div class="text-vc-header-lg font-medium">${{ formatPrice(product.price) }}</div>
					<div class="bg-background rounded-full w-1/2 md:w-1/3">
						<div
							v-if="!getCartItem(product)"
							class="flex items-center justify-center bg-accent-red text-white rounded-full cursor-pointer h-8 font-medium"
							@click="modifyCartItem(1, product)"
						>
							Agregar
						</div>

						<div v-else>
							<QuantitySelector
								:quantity="getCartItem(product).quantity"
								:maxQuantity="
									product.variant?.inventoryItem?.inventoryLevel?.quantities.find(
										(q) => q.name === 'available'
									)?.quantity || 0
								"
								@decrease="modifyCartItem(-1, product)"
								@increase="modifyCartItem(1, product)"
							/>
						</div>
					</div>
				</div>

				<div class="grid grid-cols-2 mt-6 justify-between">
					<div v-if="metadata?.Pais">
						<div class="font-medium">País</div>
						<div>{{ metadata.Pais }}</div>
					</div>
					<div v-if="metadata?.Region">
						<div class="font-medium">Región</div>
						<div>{{ metadata.Region }}</div>
					</div>
				</div>

				<div
					v-if="!metadata && product.description"
					class="pt-4"
				>
					<div class="font-medium">Descripción</div>
					<div class="whitespace-pre-wrap">{{ product.description }}</div>
				</div>

				<div
					v-if="metadata?.Dato"
					class="pt-4"
				>
					<div class="font-medium">¿Porqué está chido?</div>
					<div>{{ metadata.Dato }}</div>
				</div>

				<div
					v-if="metadata?.Perfiles"
					class="pt-4"
				>
					<div class="font-medium">Perfiles</div>

					<div class="flex flex-row overflow-x-auto space-x-2 pt-2">
						<div
							v-for="perfil in metadata.Perfiles"
							:key="perfil"
							class="flex-shrink-0 bg-accent-blue-0.25 p-1 rounded-full px-4"
						>
							{{ perfil }}
						</div>
					</div>
				</div>

				<div
					v-if="metadata?.Personalidades"
					class="pt-4"
				>
					<div class="font-medium">Personalidades</div>

					<div class="flex flex-row overflow-x-auto space-x-2 pt-2">
						<div
							v-for="personalidad in metadata.Personalidades"
							:key="personalidad"
							class="flex-shrink-0 bg-accent-green-0.25 p-1 rounded-full px-4"
						>
							{{ personalidad }}
						</div>
					</div>
				</div>

				<div
					v-if="metadata?.Situacion"
					class="pt-4"
				>
					<div class="font-medium">Situaciones</div>

					<div class="flex flex-row overflow-x-auto space-x-2 pt-2">
						<div
							v-for="situacion in metadata.Situacion.split(', ')"
							:key="situacion"
							class="flex-shrink-0 bg-accent-red-0.25 p-1 rounded-full px-4"
						>
							{{ situacion }}
						</div>
					</div>
				</div>

				<div
					v-if="metadata?.Acidez"
					class="space-y-3 pt-4 pb-16"
				>
					<div class="font-medium">Estructura</div>

					<div
						v-for="profile in profiles"
						:key="profile"
						class="flex items-center space-x-4"
					>
						<span class="text-black w-20">{{ profile }}</span>
						<div class="relative flex-1">
							<div class="h-1 bg-accent-green-0.5 rounded-full"></div>
							<div
								class="absolute top-1/2 transform -translate-y-1/2"
								:style="`left: calc((${metadata[profile]} / 5) * 100%)`"
							>
								<div
									class="w-8 h-8 bg-accent-blue text-white rounded-full flex items-center justify-center"
								>
									{{ metadata[profile] }}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useCartStore } from '../stores/cart'
import { useHead } from '@vueuse/head'
import axios from 'axios'
import ProductPhotoComponent from '@/components/ProductPhotoComponent.vue'
import QuantitySelector from '@/components/QuantitySelector.vue'
import { useToast } from 'vue-toast-notification'
import '../assets/toast.css'
import { trackViewContent } from '@/services/facebookAnalytics'

export default {
	components: {
		ProductPhotoComponent,
		QuantitySelector
	},
	setup() {
		const route = useRoute()
		const store = useCartStore()
		const product = ref(null)
		const metadata = ref(null)
		const profiles = ['Acidez', 'Cuerpo', 'Tanino', 'Frutalidad']
		const isLoading = ref(true)

		// Setup SEO metadata
		watch([product, metadata], () => {
			try {
				if (product.value) {
					const title = product.value.title.split(' - ')[0]

					// Create dynamic description
					let dynamicDescription = `${title} es un vino`

					// Add personalities if available
					if (metadata.value?.Personalidades?.length) {
						dynamicDescription += ` ${metadata.value.Personalidades.join(', ')}`.toLowerCase()
					}

					// Add profiles if available
					if (metadata.value?.Perfiles?.length) {
						dynamicDescription += ` con perfil ${metadata.value.Perfiles.join(', ')}`.toLowerCase()
					}

					// Add situations if available
					if (metadata.value?.Situacion) {
						dynamicDescription += ` perfecto para ${metadata.value.Situacion}`.toLowerCase()
					}

					useHead({
						title: `Vinos Chidos - ${title}`,
						meta: [
							{
								name: 'description',
								content: `${dynamicDescription}.`
							},
							{
								property: 'og:title',
								content: `Vinos Chidos - ${title}`
							},
							{
								property: 'og:description',
								content: `${dynamicDescription}.`
							},
							{
								property: 'og:image',
								content: product.value.image
							},
							{ property: 'og:type', content: 'website' },
							{
								name: 'keywords',
								content:
									'tiendas de vino CDMX, vinoteca Polanco, vinoteca Condesa, vinoteca San Miguel Chapultepec, dónde comprar vino, tienda de vinos chidos, horario tiendas de vino, vino tinto, vino blanco, vino rosado, vino espumoso'
							}
						]
					})
				}
			} catch (e) {
				console.error('Error setting SEO metadata:', e)
			}
		})

		function parseMetadata(product) {
			const vinoMetafield = product.metafields.find(
				(field) => field.namespace === 'meta_data_chidos' && field.key === 'vino'
			)

			if (!vinoMetafield) {
				console.warn('No metadata found for product:', product.title)
				return null
			}

			try {
				const parsedValue = JSON.parse(vinoMetafield.value)
				// Ensure all profile values are numbers
				profiles.forEach((profile) => {
					if (parsedValue[profile]) {
						parsedValue[profile] = Number(parsedValue[profile])
					}
				})
				return parsedValue
			} catch (e) {
				console.error('Error parsing metadata for product:', product.title, e)
				return null
			}
		}

		function formatPrice(price) {
			return Math.round(parseFloat(price)).toString()
		}

		function modifyCartItem(quantity, product) {
			let item = getCartItem(product)

			if (!item) {
				useToast().success('Producto agregado', { position: 'top' })
			} else if (item.quantity == 1 && quantity == -1) {
				useToast().success('Producto eliminado', { position: 'top' })
			} else {
				useToast().success('Cantidad acutalizada', { position: 'top' })
			}

			store.modifyCartItem(quantity, product)
		}

		function getCartItem(product) {
			return store.getCartItem(product)
		}

		async function fetchProduct() {
			isLoading.value = true
			try {
				const { data } = await axios.get(`/api/shopify/product?handle=${route.params.handle}`)
				product.value = data.product
				metadata.value = parseMetadata(product.value)

				// Track ViewContent event
				if (product.value) {
					trackViewContent(product.value)
				}
			} catch (err) {
				console.error('Error fetching product:', err)
			} finally {
				isLoading.value = false
			}
		}

		onMounted(() => {
			fetchProduct()
		})

		return {
			product,
			metadata,
			modifyCartItem,
			getCartItem,
			profiles,
			formatPrice,
			isLoading
		}
	}
}
</script>
